import React, {
  createContext,
  useEffect,
  useState,
  useContext,
  useMemo,
} from "react";
import { connect } from "react-redux";
import { usePrivy } from "@privy-io/react-auth";
import { sepolia } from "wagmi/chains";
import { http, createPublicClient } from "viem";
import ApiConfig from "utils/services/API/index";
import { useSmartWallets } from "@privy-io/react-auth/smart-wallets";
import appConfig from "config.json";

const WalletContext = createContext({});

// eslint-disable-next-line import/no-mutable-exports
export let logoutWallet = null;

// Component that provides wallet context when Privy is available
const PrivyWalletProvider = ({ children, jwtToken }) => {
  const [walletClient, setClient] = useState();
  const [account, setAccount] = useState();
  const [isConnecting, setIsConnecting] = useState(false);
  const { ready, authenticated, user, logout } = usePrivy();
  const { client } = useSmartWallets();

  const publicClient = useMemo(
    () =>
      createPublicClient({
        chain: sepolia,
        transport: http(appConfig.config.alchemy_rpc_url),
      }),
    []
  );

  const connectWallet = async () => {
    try {
      setIsConnecting(true);

      if (!user?.linkedAccounts) {
        throw new Error("No user accounts available");
      }
      const smartWallet = user.linkedAccounts.find(
        (userAccount) => userAccount.type === "smart_wallet"
      );
      if (!smartWallet?.address) {
        throw new Error("No smart wallet found");
      }

      // Use the client's actual address — this is the address the smart wallet
      // client can sign transactions for. The linkedAccounts address is stable
      // but may drift from the client's address when ZeroDev picks a different
      // embedded wallet signer across sessions. If they differ, the client
      // cannot transfer tokens sent to the linkedAccounts address, breaking
      // auto-deposit. So we always use what the client actually controls.
      const clientAddr = client?.account?.address;
      const activeAddr = clientAddr || smartWallet.address;
      if (clientAddr && clientAddr.toLowerCase() !== smartWallet.address.toLowerCase()) {
        console.warn("[Wallet] client address differs from linkedAccounts:", { linked: smartWallet.address, client: clientAddr, using: activeAddr });
      }

      setClient(client);
      setAccount(activeAddr);
    } catch (err) {
      console.error("Error connecting wallet:", err);
      throw err;
    } finally {
      setIsConnecting(false);
    }
  };

  const disconnect = async () => {
    setAccount(undefined);
    setClient(undefined);
    await logout();
  };

  const postWalletAddress = async () => {
    if (account) {
      await ApiConfig.post(
        `/bank/assign-wallet-address?walletAddress=${account}`
      );
    }
  };

  useEffect(() => {
    logoutWallet = disconnect;
  }, [disconnect]);

  // Connect wallet when user is available
  useEffect(() => {
    if (authenticated && ready && !account && user) {
      connectWallet();
    }
  }, [authenticated, ready, account, user]);

  // Keep walletClient in sync with Privy's smart wallet client
  // and update account if the client's address changes
  useEffect(() => {
    if (client && account) {
      if (!walletClient) {
        setClient(client);
      }
      // If the client's address changed (new session signer), update account
      const newAddr = client?.account?.address;
      if (newAddr && newAddr.toLowerCase() !== account.toLowerCase()) {
        console.warn("[Wallet] Client address changed, updating account:", { old: account, new: newAddr });
        setAccount(newAddr);
      }
    }
  }, [client, account, walletClient]);

  useEffect(() => {
    postWalletAddress();
  }, [account]);

  // Memoize the context value
  const contextValue = useMemo(
    () => ({
      ready,
      authenticated,
      user,
      logout,
      walletClient,
      disconnect,
      connectWallet,
      publicClient,
      isConnecting,
      account,
      jwtToken,
    }),
    [
      ready,
      authenticated,
      user,
      logout,
      walletClient,
      disconnect,
      connectWallet,
      publicClient,
      isConnecting,
      account,
      jwtToken,
    ]
  );

  return (
    <WalletContext.Provider value={contextValue}>
      {children}
    </WalletContext.Provider>
  );
};

// Component that provides minimal wallet context when Privy is not available
const NoPrivyWalletProvider = ({ children, jwtToken }) => {
  const publicClient = useMemo(() => {
    // Check if config is available before using it
    if (appConfig.config?.alchemy_rpc_url) {
      return createPublicClient({
        chain: sepolia,
        transport: http(appConfig.config.alchemy_rpc_url),
      });
    }
    return null;
  }, []);

  const disconnect = () => {};

  useEffect(() => {
    logoutWallet = disconnect;
  }, [disconnect]);

  // Memoize the context value with defaults
  const contextValue = useMemo(
    () => ({
      ready: false,
      authenticated: false,
      user: null,
      logout: () => {},
      walletClient: null,
      disconnect,
      connectWallet: () => {},
      publicClient,
      isConnecting: false,
      account: null,
      jwtToken,
    }),
    [publicClient, jwtToken, disconnect]
  );

  return (
    <WalletContext.Provider value={contextValue}>
      {children}
    </WalletContext.Provider>
  );
};

// Main WalletProvider that conditionally renders the appropriate provider
const WalletProvider = ({ children, jwtToken, loggedIn }) => {
  // If crypto features are disabled, always use NoPrivyWalletProvider
  if (
    !appConfig.config.bc_privy_app_id ||
    !appConfig.config.bc_privy_app_zerodev_project_id
  ) {
    return (
      <NoPrivyWalletProvider jwtToken={jwtToken}>
        {children}
      </NoPrivyWalletProvider>
    );
  }

  // If user is logged in, assume Privy is available and use PrivyWalletProvider
  // If user is not logged in, use NoPrivyWalletProvider
  if (loggedIn && jwtToken) {
    return (
      <PrivyWalletProvider jwtToken={jwtToken}>{children}</PrivyWalletProvider>
    );
  }
  return (
    <NoPrivyWalletProvider jwtToken={jwtToken}>
      {children}
    </NoPrivyWalletProvider>
  );
};

export default connect((state) => ({
  jwtToken: state.getIn(["users", "user", "token"]),
  loggedIn: state.getIn(["users", "user", "loggedIn"]),
}))(WalletProvider);

export const useWallet = () => useContext(WalletContext);
